import { createTheme } from '@mui/material/styles';

// Tons do site oficial do Hackathon (hackathon-univassouras-marica.vercel.app).
export const hackathonColors = {
  ink: '#060B18',
  ink2: '#0B1428',
  ink3: '#12203D',
  navy: '#14336E',
  wine: '#7A1530',
  brick: '#8B2C2E',
  rose: '#E0577A',
  sky: '#7FA2EE',
  paper: '#EEE9E6',
  stone: '#A39A96',
  line: 'rgba(255, 255, 255, 0.08)',
  lineStrong: 'rgba(255, 255, 255, 0.16)',
  success: '#34D399',
  warning: '#FBBF24',
  error: '#F87171',
};

const c = hackathonColors;

export const hackathonMapColors = {
  area: c.rose,
  centerFill: c.rose,
  centerStroke: c.ink,
  participant: c.sky,
  participantStroke: c.paper,
  frame: c.lineStrong,
};

export const displayFamily = '"Chakra Petch", "Plus Jakarta Sans", Inter, Arial, sans-serif';
export const monoFamily = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const bodyFamily = 'Inter, Arial, sans-serif';

export const HACKATHON_FONTS_ID = 'hackathon-fonts';
export const HACKATHON_FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@600;700&family=IBM+Plex+Mono:wght@500&display=swap';

// As fontes da marca só são pedidas quando a área do Hackathon abre.
export function ensureHackathonFonts(doc = document) {
  if (!doc?.head || doc.getElementById(HACKATHON_FONTS_ID)) return;
  const link = doc.createElement('link');
  link.id = HACKATHON_FONTS_ID;
  link.rel = 'stylesheet';
  link.href = HACKATHON_FONTS_HREF;
  doc.head.appendChild(link);
}

const focusRing = { outline: `2px solid ${c.sky}`, outlineOffset: 2 };
const chamfer = 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)';

const hackathonTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: c.rose, light: '#EA7F9A', dark: c.wine, contrastText: c.ink },
    secondary: { main: c.sky, light: '#A9C1F4', dark: c.navy, contrastText: c.ink },
    background: { default: c.ink, paper: c.ink2 },
    success: { main: c.success, contrastText: c.ink },
    warning: { main: c.warning, contrastText: c.ink },
    error: { main: c.error, contrastText: c.ink },
    info: { main: c.sky, contrastText: c.ink },
    divider: c.line,
    text: { primary: c.paper, secondary: c.stone, disabled: '#7D7774' },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: bodyFamily,
    h1: { fontFamily: displayFamily, fontSize: '1.75rem', lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.01em' },
    h2: { fontFamily: displayFamily, fontSize: '1.25rem', lineHeight: 1.25, fontWeight: 700 },
    h3: { fontFamily: displayFamily, fontSize: '1.125rem', lineHeight: 1.3, fontWeight: 700 },
    h6: { fontFamily: displayFamily, fontSize: '1rem', lineHeight: 1.35, fontWeight: 600 },
    subtitle1: { fontFamily: displayFamily, fontWeight: 600 },
    overline: { fontFamily: monoFamily, fontWeight: 500, fontSize: '0.7rem', letterSpacing: '0.14em', lineHeight: 1.6 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.45 },
    button: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: 10,
          paddingInline: 18,
          '&.Mui-focusVisible': focusRing,
        },
        contained: {
          borderRadius: 0,
          clipPath: chamfer,
          color: c.paper,
          backgroundImage: `linear-gradient(135deg, ${c.wine}, ${c.brick})`,
          backgroundColor: c.wine,
          '&:hover': { backgroundImage: `linear-gradient(135deg, #951A3B, #A3203F)` },
          // O recorte do chanfro esconde o outline: o foco usa um anel interno.
          '&.Mui-focusVisible': { outline: 'none', boxShadow: `inset 0 0 0 2px ${c.paper}, inset 0 0 0 4px ${c.rose}` },
          '&.Mui-disabled': { backgroundImage: 'none', backgroundColor: 'rgba(255,255,255,0.08)', color: '#8E8885' },
        },
        outlined: {
          color: c.paper,
          borderColor: 'rgba(127, 162, 238, 0.45)',
          '&:hover': { borderColor: c.sky, backgroundColor: 'rgba(127, 162, 238, 0.08)' },
          '&.Mui-disabled': { borderColor: c.line, color: '#8E8885' },
        },
        text: { color: c.sky },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: c.ink2, border: `1px solid ${c.line}`, borderRadius: 16 },
      },
    },
    MuiCard: {
      styleOverrides: { root: { backgroundColor: 'rgba(11, 20, 40, 0.88)' } },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 16, '&:last-child': { paddingBottom: 16 } } },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundColor: c.ink2, backgroundImage: 'none' } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: 'rgba(6, 11, 24, 0.85)' } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(18, 32, 61, 0.6)',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: c.lineStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(127, 162, 238, 0.6)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.rose, borderWidth: 2 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: c.stone, '&.Mui-focused': { color: c.rose } } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 12, alignItems: 'flex-start' } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiIconButton: {
      styleOverrides: { root: { '&.Mui-focusVisible': focusRing } },
    },
  },
});

hackathonTheme.hackathon = hackathonColors;

export default hackathonTheme;
